// The store's Follow-ups screen: what is due now, what is planned, and the
// "Add follow-up" form. The list comes from getFashionFollowUps with a due-by
// date a year out, so a follow-up planned for later shows under Upcoming.
// The server has no list of closed follow-ups for a store, so Done shows what
// was closed on this till since the screen was opened.
import { FOLLOW_UP_REASONS, daysOverdue, followUpText, lastFollowUpDay, today, validateClientDraft } from "./clientBook";
import { normaliseMobile } from "./alterations";

export const FOLLOW_UP_TABS = [
  { value: "due", label: "Due now" },
  { value: "up", label: "Upcoming" },
  { value: "done", label: "Done" },
];

// "When" on the add form → days from today.
export const WHEN_CHOICES = [
  { label: "Today", days: 0 },
  { label: "Tomorrow", days: 1 },
  { label: "In 3 days", days: 3 },
  { label: "Next week", days: 7 },
  { label: "In 2 weeks", days: 14 },
  { label: "In a month", days: 30 },
];
export const DEFAULT_WHEN = "Tomorrow";
export const REASONS = FOLLOW_UP_REASONS;

// The list call: everything open up to the furthest date a follow-up can have.
export const followUpsDueBy = (now = new Date()) => lastFollowUpDay(now);

const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export function dueDateFor(when, now = new Date()) {
  const pick = WHEN_CHOICES.find((w) => w.label === when);
  if (!pick) return "";
  return isoDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() + pick.days));
}

// A wish in stock or a follow-up due today or before is due now; a follow-up
// due later is upcoming.
export const isUpcoming = (item, now = new Date()) => item?.kind === "TASK" && (daysOverdue(item.dueDate, now) ?? 0) < 0;

export function splitFollowUps(rows, now = new Date()) {
  const due = [];
  const up = [];
  (rows || []).forEach((r) => (isUpcoming(r, now) ? up : due).push(r));
  // Most overdue first; upcoming soonest first. Wishes after the calls.
  const key = (r) => (r.kind === "TASK" ? r.dueDate || "" : "9999");
  due.sort((a, b) => key(a).localeCompare(key(b)));
  up.sort((a, b) => key(a).localeCompare(key(b)));
  return { due, up };
}

export function followUpStats(rows, done, now = new Date()) {
  const s = { over: 0, today: 0, up: 0, done: (done || []).length };
  (rows || []).forEach((r) => {
    const d = r.kind === "TASK" ? daysOverdue(r.dueDate, now) : 0;
    if (d == null || d === 0) s.today += 1;
    else if (d > 0) s.over += 1;
    else s.up += 1;
  });
  return s;
}

// Two letters for the avatar.
export const initials = (name) =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "?";

const cleanText = (s) => String(s ?? "").replace(/["\\]/g, "").replace(/[\r\n]+/g, " ").trim();

// Customer search on the add form: a phone-like entry is looked up by its
// digits (a leading 91 on a 12-digit number dropped), anything else by name.
export function customerLookupTerm(raw) {
  const t = cleanText(raw);
  if (/^[+\d\s()-]+$/.test(t) && /\d/.test(t)) return { by: "mobile", term: normaliseMobile(t) };
  return { by: "name", term: t };
}

export const MIN_MOBILE_LOOKUP = 3;
export const MIN_NAME_LOOKUP = 2;

export function canLookUpCustomer(raw) {
  const { by, term } = customerLookupTerm(raw);
  return by === "mobile" ? term.length >= MIN_MOBILE_LOOKUP : term.length >= MIN_NAME_LOOKUP;
}

export function buildCustomerLookupQuery(raw, bunitId) {
  const { term } = customerLookupTerm(raw);
  return {
    query: `query($searchKey: String!, $bunitId: String){ customerSearch(searchKey: $searchKey, bunitId: $bunitId){ cwrCustomerId code name mobileNo } }`,
    variables: { searchKey: term, bunitId: bunitId || "" },
  };
}

// Server response → up to `max` customers, or null when the call failed.
export function readCustomerHits(response, max = 6) {
  if (!response || response?.data?.errors?.length) return null;
  const rows = response?.data?.data?.customerSearch;
  if (rows == null) return [];
  if (!Array.isArray(rows)) return null;
  const seen = new Set();
  return rows
    .filter((c) => c?.cwrCustomerId && !seen.has(c.cwrCustomerId) && seen.add(c.cwrCustomerId))
    .slice(0, max)
    .map((c) => ({ customerId: c.cwrCustomerId, name: c.name || "", mobileNo: c.mobileNo || "", code: c.code || "" }));
}

// The add form → { item } ready for buildAddClientItemMutation, or { error }.
export function validateFollowUpDraft(draft, now = new Date()) {
  if (!draft?.customer?.customerId) return { error: "Pick the customer to follow up with." };
  if (!draft.reason && !cleanText(draft.note)) return { error: "Pick a reason or add a note so whoever calls knows why." };
  const dueDate = dueDateFor(draft.when, now);
  if (!dueDate) return { error: "Choose when to follow up." };
  return validateClientDraft({ kind: "TASK", bunitId: draft.bunitId, customerId: draft.customer.customerId, text: followUpText(draft.reason, draft.note), dueDate }, now);
}

export const isFutureDue = (dueDate, now = new Date()) => !!dueDate && dueDate > today(now);

// A tel: link for the Call button, or "" when there is no usable number.
export function telHref(mobileNo) {
  const raw = String(mobileNo ?? "").trim();
  const digits = raw.replace(/\D/g, "");
  if (digits.length < 6) return "";
  return `tel:${raw.startsWith("+") ? "+" : ""}${digits}`;
}
